import type { ReactNode } from "react";
import { AlertTriangle, Inbox, Loader2, WifiOff } from "lucide-react";

import { ApiError } from "@/api/client";
import { Button } from "./Button";
import { cn } from "@/lib/cn";

/** Skeleton block. Used instead of a spinner wherever the shape is known. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton", className)} aria-hidden />;
}

export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="flex flex-col gap-px" role="status" aria-label="Chargement">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: columns }).map((_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn("h-4", columnIndex === 0 ? "w-10 shrink-0" : "flex-1")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function LoadingState({ label = "Chargement…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-12 text-ink-muted" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-4 py-14 text-center">
      <div className="rounded-full bg-surface-raised p-3 text-ink-subtle">
        {icon ?? <Inbox className="size-6" aria-hidden />}
      </div>
      <div>
        <p className="text-sm font-medium text-ink">{title}</p>
        {description && <p className="mt-1 max-w-md text-[13px] text-ink-subtle">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * Error state.
 *
 * Shows the correlation id when there is one: that is the string that finds the
 * request in the server logs, and it costs nothing to surface.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null;
  const offline = apiError?.status === 0;

  return (
    <div className="flex flex-col items-center justify-center gap-3 px-4 py-14 text-center">
      <div className="rounded-full bg-danger-subtle p-3 text-danger">
        {offline ? <WifiOff className="size-6" aria-hidden /> : <AlertTriangle className="size-6" aria-hidden />}
      </div>
      <div>
        <p className="text-sm font-medium text-ink">
          {offline ? "HD-Vault est injoignable" : "Une erreur est survenue"}
        </p>
        <p className="mt-1 max-w-md text-[13px] text-ink-subtle">
          {apiError?.message ?? (error instanceof Error ? error.message : "Erreur inconnue.")}
        </p>
        {apiError?.correlationId && (
          <p className="mt-2 font-mono text-[11px] text-ink-subtle">
            Réf. {apiError.correlationId}
          </p>
        )}
      </div>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  );
}

/** Placeholder for a module the backend cannot serve yet — never a fake zero. */
export function ComingSoon({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface/40 px-4 py-6 text-center">
      <p className="text-sm font-medium text-ink-muted">{title}</p>
      <p className="mt-1 text-[12px] text-ink-subtle">{description}</p>
    </div>
  );
}
